import { useCallback, useMemo, useRef, useState } from 'react';
import arrowDown from '~/assets/icons/arrow_down.svg';
import arrowUp from '~/assets/icons/arrow_up.svg';
import settings from '~/assets/icons/settings.svg';
import trash from '~/assets/icons/trash.svg';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import { Text } from '~/components/ui/Text';
import { rage, useObservable, VehicleTechTuningItemConfig } from '~/rage';
import {
	ModalButtonColor,
	ModalType,
	TechTuningCategory,
	WalletType,
} from '~/shared';
import { useTuningDrag } from '../../hooks';
import {
	setDraggedItem,
	setDragSource,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { categories } from '../CategoryTab';
import { CategoryNamesSell } from '../CategoryTab/CategoryTab.model';
import { DragArrow } from '../DragArrow/DragArrow';
import { SetupTuningCell } from '../SetupTuningCell';
import {
	StyledBottomGradient,
	StyledButtonContainer,
	StyledContainer,
	StyledHint,
	StyledItemsList,
	StyledTopGradient,
	StyledWrapper,
} from './SetupTuning.styled';

type InstalledItemsKey = Exclude<
	TechTuningCategory,
	TechTuningCategory.SETTINGS
>;

const SetupTuning = () => {
	const dispatch = useLayoutDispatch();
	const items = useObservable(rage.techTuning.getItems());
	const installedItemsIds = useObservable(rage.techTuning.getInstalledItems());
	const [isAtTop, setIsAtTop] = useState(true);
	const [isAtBottom, setIsAtBottom] = useState(false);
	const [trashDragOver, setTrashDragOver] = useState(false);
	const itemsListRef = useRef<HTMLDivElement>(null);

	const { handleDragOver, canDropItem } = useTuningDrag();

	const draggedItem = useLayoutSelector(
		(state) => state.tuningShop.draggedItem,
	);

	const dragSource = useLayoutSelector((state) => state.tuningShop.dragSource);

	const filteredCategories = useMemo(() => {
		return categories.filter(
			(category) => category.type !== TechTuningCategory.SETTINGS,
		);
	}, []);

	const installedItemsByCategory = useMemo(() => {
		const result: Record<
			InstalledItemsKey,
			VehicleTechTuningItemConfig | null
		> = {
			[TechTuningCategory.ENGINE]: null,
			[TechTuningCategory.TURBO]: null,
			[TechTuningCategory.EXHAUST]: null,
			[TechTuningCategory.RADIATOR]: null,
			[TechTuningCategory.STARTER]: null,
			[TechTuningCategory.BRAKES]: null,
			[TechTuningCategory.SUSPENSION]: null,
			[TechTuningCategory.TIRES]: null,
		};

		// Заполняем объект установленными предметами
		installedItemsIds.forEach((id) => {
			const item = items.find((item) => item.id === id);
			if (item && item.category in result) {
				result[item.category as InstalledItemsKey] = item;
			}
		});

		return result;
	}, [installedItemsIds, items]);

	const removeDisabled = useMemo(() => {
		return (
			Object.values(installedItemsByCategory).every((item) => item === null) &&
			!draggedItem
		);
	}, [installedItemsByCategory, draggedItem]);

	const handleScroll = useCallback(() => {
		if (itemsListRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = itemsListRef.current;
			const isBottom = scrollTop + clientHeight >= scrollHeight - 1;
			const isTop = scrollTop <= 1;
			setIsAtBottom(isBottom);
			setIsAtTop(isTop);
		}
	}, []);

	const handleScrollUp = useCallback(() => {
		if (itemsListRef.current && !isAtTop) {
			itemsListRef.current.scrollTo({
				top: itemsListRef.current.scrollTop - 200,
				behavior: 'smooth',
			});
		}
	}, [isAtTop]);

	const handleScrollDown = useCallback(() => {
		if (itemsListRef.current && !isAtBottom) {
			itemsListRef.current.scrollTo({
				top: itemsListRef.current.scrollTop + 200,
				behavior: 'smooth',
			});
		}
	}, [isAtBottom]);

	const handleDrop = useCallback(
		(e: React.DragEvent, category: TechTuningCategory) => {
			e.preventDefault();

			if (!draggedItem || !canDropItem(draggedItem, category)) {
				return;
			}

			const installedItem =
				installedItemsByCategory[category as InstalledItemsKey];

			if (
				dragSource === 'equipped' &&
				installedItem &&
				installedItem.id === draggedItem.id
			) {
				dispatch(setDraggedItem(null));
				dispatch(setDragSource(null));
				return;
			}

			rage.techTuning
				.installItem(draggedItem.id)
				.catch((err) => rage.notifications.showException(err));

			dispatch(setDraggedItem(null));
			dispatch(setDragSource(null));
		},
		[draggedItem, canDropItem, dragSource, installedItemsByCategory],
	);

	const handleDragOverCell = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		(e: React.DragEvent, category: TechTuningCategory) => {
			handleDragOver(e);
		},
		[handleDragOver],
	);

	const handleCellDragStart = useCallback(
		(e: React.DragEvent, item: VehicleTechTuningItemConfig) => {
			dispatch(setDraggedItem(item));
			dispatch(setDragSource('equipped'));
			e.dataTransfer.effectAllowed = 'move';
		},
		[],
	);

	const handleCellDragEnd = useCallback(() => {
		dispatch(setDraggedItem(null));
		dispatch(setDragSource(null));
	}, [dispatch]);

	const handleTrashDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			setTrashDragOver(false);

			if (!draggedItem || !dragSource) {
				return;
			}

			const sellPrice = rage.techTuning.getItemSellPrice(draggedItem.id);

			rage.modal.showModal(
				{
					type: ModalType.Price,
					title: 'Подтверждение',
					text: `Вы действительно хотите продать ${
						CategoryNamesSell[draggedItem.category as TechTuningCategory]
					} "${draggedItem.name}"?`,
					price: sellPrice,
					wallet: WalletType.Cash,
					acceptButton: { text: 'Продать', color: ModalButtonColor.Danger },
					cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
				},
				(result) => {
					if (result.isAccepted) {
						if (dragSource === 'equipped') {
							rage.techTuning
								.sellEquipped(draggedItem.id)
								.catch((err) => rage.notifications.showException(err));
						} else {
							rage.techTuning
								.sellOwned(draggedItem.id)
								.catch((err) => rage.notifications.showException(err));
						}
					}
				},
			);
		},
		[draggedItem, dragSource],
	);

	const handleTrashDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
		setTrashDragOver(true);
	}, []);

	const handleTrashDragLeave = useCallback(() => {
		setTrashDragOver(false);
	}, []);

	return (
		<StyledWrapper>
			<StyledContainer>
				<img src={settings} />
				<Divider
					$direction={'H'}
					style={{ marginTop: 30, marginBottom: 30, opacity: 0.1 }}
				/>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height40}
					style={{ marginBottom: 20 }}
					borderRadius={ButtonBorderRadius.Radius5}
					disabled={isAtTop}
					onClick={handleScrollUp}
				>
					<img src={arrowUp} width={14} height={14} />
				</Button>
				<StyledTopGradient style={{ opacity: isAtTop ? 0 : 1 }} />
				<StyledItemsList ref={itemsListRef} onScroll={handleScroll}>
					{filteredCategories.map((category) => {
						const item =
							installedItemsByCategory[category.type as InstalledItemsKey];
						return (
							<SetupTuningCell
								key={category.id}
								category={category.type}
								item={item || undefined}
								onDragOver={(e) => handleDragOverCell(e, category.type)}
								onDrop={(e) => handleDrop(e, category.type)}
								canDrop={
									draggedItem ? canDropItem(draggedItem, category.type) : false
								}
								onDragStart={
									item ? (e) => handleCellDragStart(e, item) : undefined
								}
								onDragEnd={handleCellDragEnd}
							/>
						);
					})}
				</StyledItemsList>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height40}
					style={{ marginTop: 20 }}
					borderRadius={ButtonBorderRadius.Radius5}
					disabled={isAtBottom}
					onClick={handleScrollDown}
				>
					<img src={arrowDown} width={14} height={14} />
				</Button>
				<StyledBottomGradient style={{ opacity: isAtBottom ? 0 : 1 }} />
			</StyledContainer>
			<StyledButtonContainer>
				<Button
					color={removeDisabled ? ButtonColors.Black1 : ButtonColors.Cancel75}
					height={ButtonHeight.Height60}
					onDragOver={handleTrashDragOver}
					onDragLeave={handleTrashDragLeave}
					onDrop={handleTrashDrop}
					hover={trashDragOver}
					style={{ pointerEvents: removeDisabled ? 'none' : 'auto' }}
				>
					<img
						src={trash}
						width={20}
						height={20}
						style={{ opacity: removeDisabled ? 0.5 : 1 }}
					/>
				</Button>
				<StyledHint style={{ opacity: draggedItem ? 1 : 0 }}>
					<Text>
						Перенесите запчасть в корзину,
						<br />
						если хотите её продать
					</Text>
				</StyledHint>
			</StyledButtonContainer>
			<DragArrow />
		</StyledWrapper>
	);
};

export { SetupTuning };
